import React from 'react'

import { IconPlayDefault, TileImage } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'

import Image from 'components/LegacyImage'
import { formatTaskDuration } from 'pages/sessions/utils'
import type { Task } from '../../types'

import style from './ModuleTaskList.module.scss'

export interface ModuleTaskListProps {
  tasks: Task[]
  showContinueActivity?: boolean
}

export const ModuleTaskList = ({ tasks }: ModuleTaskListProps) => {
  const { t } = useTranslation('@mc/next')

  const hasDuration = (task: Task) => !!task.duration
  const hasMeta = (task: Task) => hasDuration(task)

  return (
    <ol data-ba='class-module'>
      {tasks.map((task) => {
        const tileContent = (
          <div className='row align-items-center'>
            <div className='col-3 d-none d-sm-block'>
              <div className={`${style.taskItemImage} mc-corners--sm`}>
                <TileImage
                  alt=''
                  image={<Image src={task.image.src} layout='fill' alt='' />}
                />
                <TaskThumbnailOverlay task={task} />
                <span className='mc-sr-only'>
                  {t(
                    task.type === 'video' || task.type === 'follow_along_video'
                      ? 'sessions.moduleTaskList.srTaskVideo'
                      : 'sessions.moduleTaskList.srTask',
                    {
                      taskNumber: task.index,
                    },
                  )}
                </span>
                <span
                  className={`${style.taskItemNumber} mc-text-h7`}
                  aria-hidden
                >
                  {task.index}
                </span>
              </div>
            </div>

            <div className='col'>
              <h5 className='mc-text-h5'>{task.title}</h5>
              {hasMeta(task) && (
                <p className='mc-text-color--gray-500 mc-my-1 mc-text-small'>
                  {typeof task.duration === 'number' && (
                    <span className={style.meta}>
                      {formatTaskDuration(task.duration)}
                    </span>
                  )}
                </p>
              )}

              <p className='mc-text-small mc-text--2-lines-max'>
                {task.description}
              </p>
            </div>
          </div>
        )

        return (
          <li
            key={`task-${task.id}`}
            className={`${style.taskItem} mc-mb-4 mc-p-4 mc-corners--sm`}
            role='group'
            aria-label='task'
          >
            {tileContent}
          </li>
        )
      })}
    </ol>
  )
}

const TaskThumbnailOverlay = ({ task }: { task: Task }) => {
  if (task.type === 'video' || task.type === 'follow_along_video') {
    return (
      <div
        aria-hidden
        className='position-absolute mc-w-100 mc-h-100 align-items-center d-flex justify-content-center'
      >
        <div
          className={`mc-corners--circle mc-p-3 d-inline-flex ${style.fakeButton}`}
        >
          <IconPlayDefault srText='' size='md' className='mc-theme-light' />
        </div>
      </div>
    )
  }
  return null
}
